
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export const useDocumentPermissions = () => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState({
    canSendMessage: false,
    canUploadFile: false,
    canCreateDocument: false,
    canShare: false,
    shareLimit: 0,
    canCreateGroups: false,
    maxGroups: 0,
    planType: 'free',
    isLoading: true,
    planStatus: 'loading' as 'loading' | 'free' | 'paid'
  });

  const checkPermissions = async () => {
    if (!user?.id) {
      setPermissions({
        canSendMessage: false,
        canUploadFile: false,
        canCreateDocument: false,
        canShare: false,
        shareLimit: 0,
        canCreateGroups: false,
        maxGroups: 0,
        planType: 'free',
        isLoading: false,
        planStatus: 'free'
      });
      return;
    }

    try {
      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('plan_type')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const planType = subscription?.plan_type || 'free';
      const isFree = planType === 'free';
      const planStatus = isFree ? 'free' : 'paid';

      // Definir limites de compartilhamento por plano
      const getShareLimits = (plan: string) => {
        switch (plan) {
          case 'free':
            return { canShare: false, shareLimit: 0, canCreateGroups: false, maxGroups: 0 };
          case 'basic':
            return { canShare: true, shareLimit: 1, canCreateGroups: false, maxGroups: 0 };
          case 'pro':
            return { canShare: true, shareLimit: 10, canCreateGroups: true, maxGroups: 5 };
          case 'premium':
            return { canShare: true, shareLimit: -1, canCreateGroups: true, maxGroups: -1 };
          default:
            return { canShare: false, shareLimit: 0, canCreateGroups: false, maxGroups: 0 };
        }
      };

      const shareLimits = getShareLimits(planType);

      // DEBUG: Log da consulta e resultado
      console.log('🔍 DEBUG useDocumentPermissions - Dados:', {
        subscription,
        planType,
        isFree,
        planStatus,
        shareLimits,
        userId: user.id
      });

      setPermissions({
        canSendMessage: !isFree,
        canUploadFile: !isFree,
        canCreateDocument: true,
        canShare: shareLimits.canShare,
        shareLimit: shareLimits.shareLimit,
        canCreateGroups: shareLimits.canCreateGroups,
        maxGroups: shareLimits.maxGroups,
        planType,
        isLoading: false,
        planStatus
      });
    } catch (error) {
      console.error('Erro ao verificar permissões:', error);
      setPermissions({
        canSendMessage: false,
        canUploadFile: false,
        canCreateDocument: false,
        canShare: false,
        shareLimit: 0,
        canCreateGroups: false,
        maxGroups: 0,
        planType: 'free',
        isLoading: false,
        planStatus: 'free'
      });
    }
  };

  useEffect(() => {
    checkPermissions();
  }, [user?.id]);

  // Listener para mudanças na tabela subscriptions em tempo real
  useEffect(() => {
    if (!user?.id) return;
    
    const channel = supabase
      .channel(`subscription-changes-${user.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'subscriptions',
        filter: `user_id=eq.${user.id}`
      }, () => {
        console.log('Subscription alterada - recarregando permissões');
        checkPermissions();
      })
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  return permissions;
};
